import { writeAuditLog, findActiveIssueByTitle, markIssueObserved, createIssue } from '@wave-circuit/core';

/**
 * Log a structured audit event to the 'admin_audit_logs' collection.
 * 
 * @param {Object} params
 * @param {string} params.action - e.g. 'user_login', 'page_navigation', 'security_check', 'data_update', 'diagnostic_run'
 * @param {string} params.category - 'security' | 'auth' | 'data' | 'system' | 'navigation' | 'api' | 'diagnostic'
 * @param {string} params.severity - 'info' | 'warning' | 'error' | 'critical'
 * @param {string} [params.target] - The target resource (e.g. 'users/123', 'opportunities', '/business/dashboard')
 * @param {string} [params.message] - Human-readable summary
 * @param {Object} [params.details] - Additional contextual payload
 */
export async function logAuditEvent({
    action,
    category = 'system',
    severity = 'info',
    target = '',
    message = '',
    details = {}
}) {
    try {
        // Signed-out visitors aren't logged (writeAuditLog resolves null).
        return await writeAuditLog({
            action,
            category,
            severity,
            target,
            message: String(message || `${action} in ${category}`).slice(0, 2000),
            details: typeof details === 'object' ? details : { raw: String(details) },
            path: window.location.pathname,
            // Query strings and fragments can carry tokens; log the page only.
            url: `${window.location.origin}${window.location.pathname}`,
            userAgent: navigator.userAgent,
            clientTime: new Date().toISOString()
        });
    } catch (err) {
        // Fallback to console to avoid recursive failure loops
        console.warn('Audit logger fallback:', err?.message || err);
        return null;
    }
}

/**
 * Log an identified bug into audit logs and file to issue tracker ONLY ONCE (deduplicated).
 */
export async function logSystemBug({
    title,
    description,
    page = '',
    severity = 'high',
    stackTrace = '',
    category = 'bug',
    metadata = {}
}) {
    const issueTitle = `[Auto-Detected] ${title}`;

    // 1. Write to audit log
    const auditRecord = await logAuditEvent({
        action: 'bug_detected',
        category: 'system',
        severity: severity === 'critical' ? 'critical' : 'error',
        target: page || window.location.pathname,
        message: `Identified Bug: ${title}`,
        details: {
            title,
            description,
            page,
            stackTrace,
            category,
            ...metadata
        }
    });

    // 2. Deduplicate: Check if an open/in-progress issue with this title already exists
    try {
        const activeIssue = await findActiveIssueByTitle(issueTitle);
        if (activeIssue) {
            // Already flagged — update timestamp only, do NOT duplicate
            await markIssueObserved(activeIssue.id);
            return auditRecord;
        }

        // 3. Register issue once if not already open
        await createIssue({
            title: issueTitle,
            description: `${description}\n\n**Page / Path:** ${page || 'N/A'}\n**Stack / Details:**\n\`\`\`\n${stackTrace || 'No stack trace'}\n\`\`\``,
            severity: severity,
            status: 'open',
            category: category,
            assignedTo: '',
            reportedBy: auditRecord?.actorEmail || 'system-scanner',
            autoDetected: true
        });
    } catch (e) {
        console.warn('Failed to auto-register issue:', e);
    }

    return auditRecord;
}

/**
 * Initialize window unhandled error and rejection listeners to capture client bugs.
 */
let isListenerInitialized = false;
const MAX_GLOBAL_ERRORS_PER_PAGE_LOAD = 5;
const reportedErrors = new Set();

// A render loop can throw thousands of identical errors; report each once, and only a few in total.
function shouldReport(message) {
    if (reportedErrors.size >= MAX_GLOBAL_ERRORS_PER_PAGE_LOAD || reportedErrors.has(message)) return false;
    reportedErrors.add(message);
    return true;
}

export function initGlobalErrorLogging() {
    if (isListenerInitialized || typeof window === 'undefined') return;
    isListenerInitialized = true;

    window.addEventListener('error', (event) => {
        // Skip extension-induced errors
        if (event.filename && (event.filename.includes('chrome-extension://') || event.filename.includes('moz-extension://'))) {
            return;
        }
        if (!shouldReport(event.message || 'runtime-error')) return;

        logAuditEvent({
            action: 'unhandled_runtime_error',
            category: 'system',
            severity: 'error',
            target: event.filename || window.location.pathname,
            message: event.message || 'Unhandled JavaScript runtime error',
            details: {
                message: event.message,
                filename: event.filename,
                lineno: event.lineno,
                colno: event.colno,
                stack: event.error?.stack || 'N/A'
            }
        });
    });

    window.addEventListener('unhandledrejection', (event) => {
        const reason = event.reason;
        const message = reason?.message || String(reason) || 'Unhandled Promise rejection';
        if (!shouldReport(message)) return;

        logAuditEvent({
            action: 'unhandled_promise_rejection',
            category: 'api',
            severity: 'warning',
            target: window.location.pathname,
            message: `Promise Rejection: ${message}`,
            details: {
                message,
                stack: reason?.stack || 'N/A'
            }
        });
    });
}
